import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 28000-28999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const fileSystemErrors = {
  // 28xxx is the fileSystemServer module's series. Six of its sites needed no new code: three reused
  // the shared FILE_NOT_FOUND (25025), two FOLDER_NOT_FOUND (25008) and one SPACE_NOT_FOUND (7015),
  // each verbatim except for a trailing period at one site (non-material). Two stay generic — one
  // literally says "Something went wrong", one forwards a child service's message.
  // 28001-28018 — the fileSystemServer module's split. Messages copied verbatim.
  FS_NO_FILES_SELECTED_TO_MOVE: {
    errorCode: 28001,
    errorMessage: t("No files were selected to move"),
    isDynamic: false,
  },
  // Two sibling guards on the same rule, one shouting in caps and one a sentence. Kept apart
  // because they are genuinely different strings on the wire today; harmonisation candidates.
  FS_CANNOT_MOVE_TO_DIFFERENT_SPACE_CAPS: {
    errorCode: 28002,
    errorMessage: t("CANNOT MOVE TO DIFFERENT SPACE"),
    isDynamic: false,
  },
  FS_FILES_CANNOT_MOVE_DIFFERENT_SPACE: {
    errorCode: 28003,
    errorMessage: t("Files can't be moved into a different Space."),
    isDynamic: false,
  },
  FS_CANNOT_PERFORM_ACTION: {
    errorCode: 28004,
    errorMessage: t("CANNOT PERFORM THAT ACTION"),
    isDynamic: false,
  },
  FS_DESTINATION_IS_SUBFOLDER_OF_SOURCE: {
    errorCode: 28005,
    errorMessage: t("The destination folder is the subfolder of the source folder."),
    isDynamic: false,
  },
  // Plural/singular upload guards are separate sites with separate sentences, not a pluralisation.
  FS_NO_FILES_RECEIVED_TO_UPLOAD: {
    errorCode: 28006,
    errorMessage: t("No files were received to upload."),
    isDynamic: false,
  },
  FS_SYNC_DESKTOP_ID_MISSING: {
    errorCode: 28007,
    errorMessage: t("The sync request did not carry a desktopId."),
    isDynamic: false,
  },
  FS_SYSTEM_INFO_NOT_OBJECT: {
    errorCode: 28008,
    errorMessage: t("The systemInfo sent with this request is not a JSON object."),
    isDynamic: false,
  },
  FS_NO_FILE_RECEIVED_TO_UPLOAD: {
    errorCode: 28009,
    errorMessage: t("No file was received to upload."),
    isDynamic: false,
  },
  FS_NO_VALID_FILE_IN_UPLOAD: {
    errorCode: 28010,
    errorMessage: t("No valid file was found in the upload request."),
    isDynamic: false,
  },
  FS_IDS_MUST_BE_OBJECT_IDS: {
    errorCode: 28011,
    errorMessage: t("fileVersionIds, folderIds and issuesIds must be 24-character hex ObjectIds."),
    isDynamic: false,
  },
  FS_FOLDER_CREATE_FAILED: {
    errorCode: 28012,
    errorMessage: t("Folder cannot be created"),
    isDynamic: false,
  },
  FS_NO_FILE_SELECTED_TO_RENAME: {
    errorCode: 28013,
    errorMessage: t("No file was selected to rename"),
    isDynamic: false,
  },
  FS_NO_FOLDER_SELECTED_TO_READ: {
    errorCode: 28014,
    errorMessage: t("No folder was selected to read."),
    isDynamic: false,
  },
  // 28015 (FS_NO_EXTERNAL_VOLUME) is retired: nothing sends it. Do not reuse the number.
  FS_PROJECT_NO_ACCESS: {
    errorCode: 28016,
    errorMessage: t("You do not have access to this project."),
    isDynamic: false,
  },
  FS_DOCUMENT_READ_FAILED: {
    errorCode: 28017,
    errorMessage: t("The document could not be read from storage."),
    isDynamic: false,
  },
  FS_SPREADSHEET_CACHE_READ_FAILED: {
    errorCode: 28018,
    errorMessage: t("The cached spreadsheet conversion could not be read."),
    isDynamic: false,
  },
  FS_FILE_ALREADY_EXISTS: {
    errorCode: 28019,
    errorMessage: t("File Already Exists"),
    isDynamic: false,
  },
  // 28020-28023: the rename request's four id guards, one per resource kind.
  FS_RENAME_SCHEDULE_ID_MISSING: {
    errorCode: 28020,
    errorMessage: t("The Schedule id is missing from the rename request."),
    isDynamic: false,
  },
  FS_RENAME_ESTIMATE_ID_MISSING: {
    errorCode: 28021,
    errorMessage: t("The Estimate id is missing from the rename request."),
    isDynamic: false,
  },
  FS_RENAME_CONTRACT_ID_MISSING: {
    errorCode: 28022,
    errorMessage: t("The Contract id is missing from the rename request."),
    isDynamic: false,
  },
  FS_RENAME_RESOURCE_ID_MISSING: {
    errorCode: 28023,
    errorMessage: t("The file or folder id is missing from the rename request."),
    isDynamic: false,
  },
  // 28024-28028: the document-conversion routes and the name/copy refusals.
  FS_DOCUMENT_KEY_REQUIRED: {
    errorCode: 28024,
    errorMessage: t("No document key was supplied"),
    isDynamic: false,
  },
  FS_DOCUMENT_NOT_FOUND: {
    errorCode: 28025,
    errorMessage: t("Document not found"),
    isDynamic: false,
  },
  FS_DOCUMENT_UNDER_PROCESSING: {
    errorCode: 28026,
    errorMessage: t("Document under Processing"),
    isDynamic: false,
  },
  FS_FILES_COPY_FAILED: {
    errorCode: 28027,
    errorMessage: t("The selected files could not be copied."),
    isDynamic: false,
  },
  FS_NAME_INVALID_CHARACTERS: {
    errorCode: 28028,
    errorMessage: t(
      'The file or folder name contains invalid characters. Please use a different name. Valid file or folder name cannot end with a dot or space, and cannot contain any of the following characters: \\\\ / : * ? " < > |',
    ),
    isDynamic: false,
  },
  // 28029: createFolder / rename name clash.
  FS_NAME_ALREADY_EXISTS: {
    errorCode: 28029,
    errorMessage: t("A file or folder with the name {{name}} already exists."),
    isDynamic: true,
    dynamicParams: ["name"],
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
